import {
  useQuery,
  useQueryClient,
  useMutation,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  activateConsult,
  getCaseManagerActiveConsults,
  getCaseManagerPartnerFacilityFilters,
  getCaseManagerPastConsults,
} from '../services/caseManagerServices';
import type {
  ActivateConsultResponse,
  ConsultResponse,
  PartnerFacilityFilterResponse,
} from '../services/caseManagerServices';

type ConsultOptions = Omit<
  UseQueryOptions<ConsultResponse, Error>,
  'queryKey' | 'queryFn'
>;

export const useCaseManagerActiveConsults = (
  userId: number,
  options?: ConsultOptions
) => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<ConsultResponse>({
      queryKey: ['CaseManagerActiveConsults', userId],
      queryFn: async () => {
        const data = await getCaseManagerActiveConsults(userId);
        return data;
      },

      staleTime: 0,
      gcTime: 0,
      ...options,
    });

  return {
    CaseManagerActiveConsults: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useCaseManagerPastConsults = (
  userId: number,
  options?: ConsultOptions
) => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<ConsultResponse>({
      queryKey: ['CaseManagerPastConsults', userId],
      queryFn: async () => {
        const data = await getCaseManagerPastConsults(userId);
        return data;
      },
      staleTime: 0,
      gcTime: 0,
      enabled: false,
      ...options,
    });

  return {
    CaseManagerPastConsults: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useCaseManagerPartnerFacilityFilters = (userId: number) => {
  const { data, isLoading, isError, error, refetch } =
    useQuery<PartnerFacilityFilterResponse>({
      queryKey: ['CaseManagerPartnerFacilityFilters', userId],
      queryFn: async () => {
        const data = await getCaseManagerPartnerFacilityFilters(userId);
        return data;
      },
      staleTime: 0,
      gcTime: 0,
      enabled: true,
    });

  return {
    CaseManagerPartnerFacilityFilters: data,
    isLoading,
    isError,
    error,
    refetch,
  };
};

export const useActivateConsult = (userId: number, onSuccess: () => void) => {
  const queryClient = useQueryClient();

  return useMutation<
    ActivateConsultResponse,
    { status: number; message: string },
    { consultId: number } // Arguments type
  >({
    mutationFn: async ({ consultId }) => {
      const data = await activateConsult(consultId, userId.toString());
      console.log('data', data);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['activateConsultResponse'],
      });
      onSuccess();
    },
    onError: (error) => {
      console.error('Error accepting consult:', error);
    },
  });
};
